import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Alert, 
  List, 
  ListItem, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';

interface Call {
  id: number;
  date: string;
  manager_name: string;
  project_name: string;
  customer_name: string;
  phone: string;
  comment?: string;
  mp3_url?: string;
  is_sent: boolean;
  approve: boolean;
  has_duplicates: boolean;
  lead_id?: number;
  source?: 'obzvonilka' | 'skorozvon';
}

export const CallsPage: React.FC = () => {
  const [calls, setCalls] = useState<Call[]>([]);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [playingCallId, setPlayingCallId] = useState<number | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [stats, setStats] = useState<any>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    customer_name: '',
    phone: '',
    comment: '',
    approve: false
  });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Состояние для фильтров
  const [filters, setFilters] = useState({
    approve: 'all', // 'all', 'approved', 'not_approved'
    is_sent: 'all', // 'all', 'sent', 'not_sent'
    source: 'all',  // 'all', 'obzvonilka', 'skorozvon'
  });

  useEffect(() => {
    fetchCalls();
    fetchStats();
  }, []);

  // Функция фильтрации звонков
  const filteredCalls = calls.filter(call => {
    // Фильтр по одобрению
    if (filters.approve !== 'all') {
      const isApproved = filters.approve === 'approved';
      if (call.approve !== isApproved) return false;
    }

    // Фильтр по отправке
    if (filters.is_sent !== 'all') {
      const isSent = filters.is_sent === 'sent';
      if (call.is_sent !== isSent) return false;
    }

    // Фильтр по источнику
    if (filters.source !== 'all') {
      const source = call.source || 'obzvonilka';
      if (source !== filters.source) return false;
    }

    return true;
  });

  const fetchCalls = async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`/calls`);
      setCalls(res.data);
    } catch (error) {
      setNotification('Ошибка при загрузке звонков');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await apiService.get(`/calls/stats`);
      setStats(res.data);
    } catch (error) {
      setNotification('Ошибка при загрузке статистики');
    }
  };

  const playAudio = async (callId: number) => {
    if (audioElement) {
      audioElement.pause();
    }

    try {
      const res = await apiService.get(`/calls/${callId}/recording`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const audio = new Audio(url);
      audio.playbackRate = playbackSpeed;
      await audio.play();
      setPlayingCallId(callId);
      setAudioElement(audio);

      audio.onended = () => {
        setPlayingCallId(null);
        setAudioElement(null);
        URL.revokeObjectURL(url);
      };
    } catch (e: any) {
      setNotification(`Не удалось загрузить запись: ${e?.message || e}`);
    }
  };

  const stopAudio = () => {
    if (audioElement) {
      audioElement.pause();
      setPlayingCallId(null);
      setAudioElement(null);
    }
  };

  const handleSpeedChange = (newSpeed: number) => {
    setPlaybackSpeed(newSpeed);
    if (audioElement && playingCallId) {
      audioElement.playbackRate = newSpeed;
    }
  };

  const updateCall = async (callId: number, data: Partial<Call>) => {
    try {
      await apiService.put(`/calls/${callId}`, data);
      
      // Проверяем, был ли создан лид
      const shouldCreateLead = 
        (data.approve === true && !selectedCall?.approve) ||
        (data.is_sent === true && !selectedCall?.is_sent);
      
      if (shouldCreateLead) {
        setNotification('Звонок обновлен и лид создан!');
      } else {
        setNotification('Звонок обновлен');
      }
      
      setEditDialogOpen(false);
      fetchCalls();
    } catch (error) {
      setNotification('Ошибка при обновлении звонка');
    }
  };

  const deleteCall = async (callId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот звонок?')) {
      return;
    }

    try {
      await apiService.delete(`/calls/${callId}`);
      setNotification('Звонок удален');
      fetchCalls();
    } catch (error) {
      setNotification('Ошибка при удалении звонка');
    }
  };

  const handleEditClick = (call: Call) => {
    setSelectedCall(call);
    setEditFormData({
      customer_name: call.customer_name,
      phone: call.phone,
      comment: call.comment || '',
      approve: call.approve
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCall) {
      updateCall(selectedCall.id, editFormData);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
        Управление звонками
      </Typography>

      {notification && (
        <Alert 
          severity={notification.includes('Ошибка') ? 'error' : 'info'} 
          onClose={() => setNotification(null)}
          sx={{ mb: 2 }}
        >
          {notification}
        </Alert>
      )}

      {stats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Статистика звонков
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fit, minmax(150px, 1fr))' }, gap: 2 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="white">
                  {stats.total_calls || 0}
                </Typography>
                <Typography variant="caption" color="white" display="block">
                  Всего
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="white">
                  {stats.sent_calls || 0}
                </Typography>
                <Typography variant="caption" color="white" display="block">
                  Отправлено
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="white">
                  {stats.approved_calls || 0}
                </Typography>
                <Typography variant="caption" color="white" display="block">
                  Одобрено
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="white">
                  {typeof stats.approval_rate === 'number' ? stats.approval_rate.toFixed(1) : '0.0'}%
                </Typography>
                <Typography variant="caption" color="white" display="block">
                  Одобрение
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ mb: 2 }}>
        <Button 
          variant="contained" 
          onClick={fetchCalls}
          disabled={loading}
          fullWidth={isMobile}
        >
          {loading ? 'Обновление...' : 'Обновить список'}
        </Button>
        <Button 
          variant="outlined" 
          onClick={fetchStats}
          fullWidth={isMobile}
        >
          Обновить статистику
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Список звонков ({filteredCalls.length} из {calls.length})
          </Typography>
          
          {/* Фильтры */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Фильтры:
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, width: isMobile ? '100%' : 'auto' }}>
              {/* Фильтр по одобрению */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Typography variant="caption">Одобр.:</Typography>
                <select
                  value={filters.approve}
                  onChange={(e) => setFilters(prev => ({ ...prev, approve: e.target.value }))}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', flex: 1 }}
                >
                  <option value="all">Все</option>
                  <option value="approved">Одобренные</option>
                  <option value="not_approved">Не одобренные</option>
                </select>
              </Box>
              
              {/* Фильтр по отправке */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Typography variant="caption">Отпр.:</Typography>
                <select
                  value={filters.is_sent}
                  onChange={(e) => setFilters(prev => ({ ...prev, is_sent: e.target.value }))}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', flex: 1 }}
                >
                  <option value="all">Все</option>
                  <option value="sent">Отправленные</option>
                  <option value="not_sent">Не отправленные</option>
                </select>
              </Box>

              {/* Фильтр по источнику */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Typography variant="caption">Источник:</Typography>
                <select
                  value={filters.source}
                  onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', flex: 1 }}
                >
                  <option value="all">Все</option>
                  <option value="obzvonilka">Обзвонилка</option>
                  <option value="skorozvon">Скорозвон</option>
                </select>
              </Box>
            </Box>

            {/* Кнопка сброса фильтров */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => setFilters({ approve: 'all', is_sent: 'all', source: 'all' })}
              fullWidth={isMobile}
            >
              Сбросить фильтры
            </Button>
          </Box>
          
          {filteredCalls.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
              {calls.length === 0 ? 'Звонки не найдены' : 'Нет звонков, соответствующих фильтрам'}
            </Typography>
          ) : (
            <List>
              {filteredCalls.map((call) => (
                <ListItem 
                  key={call.id}
                  sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    mb: 1,
                    p: isMobile ? 1 : 2,
                    bgcolor: call.has_duplicates ? '#fff3cd' : 'background.paper',
                    flexDirection: 'column',
                    alignItems: 'stretch'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                        {call.customer_name}
                      </Typography>
                      <Typography variant="body2" component="div" color="text.secondary">
                        {call.phone}
                        {call.has_duplicates && (
                          <Chip label="DUP" color="warning" size="small" sx={{ ml: 1, height: 20, fontSize: 10 }} />
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton onClick={() => handleEditClick(call)} color="primary" size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => deleteCall(call.id)} color="error" size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Менеджер: {call.manager_name} | Проект: {call.project_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Дата: {new Date(call.date).toLocaleString()}
                    </Typography>
                  </Box>

                  {(call.mp3_url || call.source === 'skorozvon') && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, bgcolor: '#f5f5f5', p: 0.5, borderRadius: 1 }}>
                      <IconButton
                        onClick={() => playingCallId === call.id ? stopAudio() : playAudio(call.id)}
                        color="primary"
                        size="small"
                      >
                        {playingCallId === call.id ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                      </IconButton>
                      <FormControl size="small" sx={{ minWidth: 70 }}>
                        <Select
                          value={playbackSpeed}
                          onChange={(e) => handleSpeedChange(e.target.value as number)}
                          sx={{ height: 28, fontSize: 12 }}
                        >
                          <MenuItem value={1}>1x</MenuItem>
                          <MenuItem value={1.25}>1.25x</MenuItem>
                          <MenuItem value={1.5}>1.5x</MenuItem>
                          <MenuItem value={2}>2x</MenuItem>
                        </Select>
                      </FormControl>
                      <Box sx={{ flex: 1 }} />
                      <Button
                        size="small"
                        variant={call.approve ? "outlined" : "contained"}
                        color={call.approve ? "success" : "primary"}
                        onClick={() => updateCall(call.id, { approve: !call.approve })}
                        sx={{ fontSize: 10, py: 0.5 }}
                      >
                        {call.approve ? 'Одобрен' : 'Одобрить'}
                      </Button>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={call.is_sent ? 'Отправлен' : 'Не отпр.'} 
                      color={call.is_sent ? 'success' : 'default'} 
                      size="small"
                      sx={{ height: 20, fontSize: 10 }}
                    />
                    {call.lead_id && (
                      <Chip 
                        label={`Лид ID: ${call.lead_id}`} 
                        color="info" 
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    )}
                  </Box>

                  {call.comment && (
                    <Typography variant="body2" sx={{ mt: 1, fontSize: 12, p: 1, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                      {call.comment}
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Диалог редактирования */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Редактировать звонок
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Имя клиента"
              value={editFormData.customer_name}
              onChange={(e) => setEditFormData(prev => ({ ...prev, customer_name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Телефон"
              value={editFormData.phone}
              onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Комментарий"
              multiline
              rows={3}
              value={editFormData.comment}
              onChange={(e) => setEditFormData(prev => ({ ...prev, comment: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editFormData.approve}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, approve: e.target.checked }))}
                />
              }
              label="Одобрен"
            />

          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>
              Отмена
            </Button>
            <Button type="submit" variant="contained">
              Сохранить
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}; 